import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CertificationProviderSearchInterface } from 'app/library/certification-provider/interfaces/certification-provider-search.interface';
import { CertificationProviderSearchCommand } from 'app/library/certification-provider/models/certification-provider-search.command.model';
import { INotifySelectedParticipantsActionPayload } from 'app/library/module/interfaces/notfiy-selected.action.interface';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { FileCommand } from 'app/projects/core/src/lib/models/file.command';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { Moment } from 'moment';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ICertificateRecordQuery } from '../interfaces/certificate-record.query.interface';
import { CertificateRecord } from '../models/certificate-record';
import { CertificateRecordCommand } from '../models/certificate-record.command.model';
import { UpdateCertificateRecordCommand } from '../models/update-certificate-record.command.model';

export interface IManagePostCertificateRecordActionPayload {
    existing: ICertificateRecordQuery[];
    new: ICertificateRecordQuery[];
    replaced: ICertificateRecordQuery[];
}

export interface ICertificateRecordsAndTotalCount {
    data: ICertificateRecordQuery[];
    pagination: {
        number_of_pages: number;
        number_of_results: number;
    };
}

@Injectable()
export class CertificateRecordApiService extends EntityService<ICertificateRecordQuery, CertificateRecordCommand> {
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {
        super(http, environment.apiUrl, 'certificates');
    }

    addCertificate(
        certificationId: string,
        issueDate: Moment,
        expirationDate: Moment,
        userId: string,
        file: File,
        notifyAboutExpiration: boolean,
        sendNotification: boolean
    ): Observable<{ existing: CertificateRecord[]; newlyAssigned: CertificateRecord[]; replaced: CertificateRecord[] }> {
        let params: HttpParams;

        if (sendNotification || sendNotification === false) {
            params = new HttpParams().set('send_notification', sendNotification.toString());
        }
        const data = new CertificateRecordCommand(certificationId, issueDate, expirationDate, file, notifyAboutExpiration).formData;

        return this.http.post<IManagePostCertificateRecordActionPayload>(`${this.environment.apiUrl}/users/${userId}/certificates`, data, { params }).pipe(
            map((queries) => {
                return {
                    existing: queries.existing.map((query) => new CertificateRecord(query)),
                    newlyAssigned: queries.new.map((query) => new CertificateRecord(query)),
                    replaced: queries.replaced.map((query) => new CertificateRecord(query)),
                };
            })
        );
    }

    assignCertificateToModuleUser(
        participantId: string,
        certificationId: string,
        issueDate: Moment,
        expirationDate: Moment,
        sendNotification: boolean,
        file: File
    ): Observable<{ existing: CertificateRecord[]; newlyAssigned: CertificateRecord[]; replaced: CertificateRecord[] }> {
        const data = new CertificateRecordCommand(certificationId, issueDate, expirationDate, file).formData;

        return this.http
            .post<IManagePostCertificateRecordActionPayload>(`${this.environment.apiUrl}/participants/${participantId}/certificates?send_notification=${sendNotification}`, data)
            .pipe(
                map((queries) => {
                    return {
                        existing: queries.existing.map((query) => new CertificateRecord(query)),
                        newlyAssigned: queries.new.map((query) => new CertificateRecord(query)),
                        replaced: queries.replaced.map((query) => new CertificateRecord(query)),
                    };
                })
            );
    }

    assignCertificateToModuleUsers(
        moduleUserIds: string[],
        moduleCertificationIds: string[],
        sendNotification: boolean
    ): Observable<{ existing: CertificateRecord[]; newlyAssigned: CertificateRecord[]; replaced: CertificateRecord[] }> {
        const data = { module_certifications_ids: moduleCertificationIds.map((id) => parseInt(id)), module_user_ids: moduleUserIds.map((id) => parseInt(id)) };

        return this.http
            .post<IManagePostCertificateRecordActionPayload>(`${this.environment.apiUrl}/participants/certificates/bulk?send_notification=${sendNotification}`, data)
            .pipe(
                map((queries) => {
                    return {
                        existing: queries.existing.map((query) => new CertificateRecord(query)),
                        newlyAssigned: queries.new.map((query) => new CertificateRecord(query)),
                        replaced: queries.replaced.map((query) => new CertificateRecord(query)),
                    };
                })
            );
    }

    getSingleCertificate(id: string): Observable<CertificateRecord> {
        return this.http.get<ICertificateRecordQuery>(`${this.apiUrl}/${this.entities}/${id}`).pipe(map((certificate) => new CertificateRecord(certificate)));
    }

    getCertificateFile(id: string): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/certificate/${this.entities}/${id}`, { responseType: 'blob' });
    }

    getCertificatesToReplace(userId: string, certificationId: string, expirationDate: string): Observable<CertificateRecord[]> {
        return this.http
            .get<ICertificateRecordQuery[]>(`${this.apiUrl}/users/${userId}/certifications/${certificationId}/certificates-to-replace?expirationDate=${expirationDate}`)
            .pipe(map((queries) => queries.map((query) => new CertificateRecord(query))));
    }

    update(certificateRecordId: string, certificationId: string, issueDate: Moment, expirationDate: Moment, notifyAboutExpiration: boolean): Observable<CertificateRecord> {
        const data = new UpdateCertificateRecordCommand(certificationId, issueDate, expirationDate, notifyAboutExpiration);

        return this.http
            .put<ICertificateRecordQuery>(`${this.environment.apiUrl}/${this.entities}/${certificateRecordId}`, data)
            .pipe(map((query) => new CertificateRecord(query)));
    }

    async uploadCertificateFile(certificateRecordId: string, file: File): Promise<CertificateRecord> {
        const data = new FileCommand('file', new File([file], '', { type: file.type }), file.name).formData;

        return this.http
            .post<ICertificateRecordQuery>(`${this.environment.apiUrl}/${this.entities}/${certificateRecordId}/upload-file`, data)
            .pipe(map((query) => new CertificateRecord(query)))
            .toPromise();
    }

    async getCertificatesStatistics(
        pageSize = 10,
        pageIndex = 1,
        search = '',
        payload: CertificationProviderSearchInterface
    ): Promise<{
        totalCount: number;
        expiredCount: number;
        expiringCount: number;
    }> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        const data = new CertificationProviderSearchCommand(payload);

        return this.http
            .post<{
                total_count: number;
                expired_count: number;
                expiring_count: number;
            }>(`${this.environment.apiUrl}/certificates/statistics`, data, { params })
            .pipe(
                map((query) => {
                    return {
                        totalCount: query.total_count,
                        expiredCount: query.expired_count,
                        expiringCount: query.expiring_count,
                    };
                })
            )
            .toPromise();
    }

    async getTeamCertificatesStatistics(
        teamId: string,
        pageSize = 10,
        pageIndex = 1,
        search = '',
        payload: CertificationProviderSearchInterface,
        includeSubteams: boolean = false
    ): Promise<{
        totalCount: number;
        expiredCount: number;
        expiringCount: number;
    }> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (includeSubteams) {
            params = params.set('includeSubteams', true.toString());
        }

        const data = new CertificationProviderSearchCommand(payload);

        return this.http
            .post<{
                total_count: number;
                expired_count: number;
                expiring_count: number;
            }>(`${this.environment.apiUrl}/teams/${teamId}/certificates/statistics`, data, { params })
            .pipe(
                map((query) => {
                    return {
                        totalCount: query.total_count,
                        expiredCount: query.expired_count,
                        expiringCount: query.expiring_count,
                    };
                })
            )
            .toPromise();
    }

    async getUserCertificatesStatistics(
        userId: string,
        pageSize = 10,
        pageIndex = 1,
        search = '',
        payload: CertificationProviderSearchInterface
    ): Promise<{
        totalCount: number;
        expiredCount: number;
        expiringCount: number;
    }> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        const data = new CertificationProviderSearchCommand(payload);

        return this.http
            .post<{
                total_count: number;
                expired_count: number;
                expiring_count: number;
            }>(`${this.environment.apiUrl}/users/${userId}/certificates/statistics`, data, { params })
            .pipe(
                map((query) => {
                    return {
                        totalCount: query.total_count,
                        expiredCount: query.expired_count,
                        expiringCount: query.expiring_count,
                    };
                })
            )
            .toPromise();
    }

    downloadCertificate(certificateId: string): Observable<Blob> {
        return this.http
            .get(`${this.environment.apiUrl}/certificate/certificates/${certificateId}`, { responseType: 'blob' })
            .pipe(tap((file) => MaxBrainUtils.downloadFile(file)));
    }

    getUserCourseCertificates(
        pageSize = 10,
        pageIndex = 1,
        search = '',
        userId: string,
        sortBy?: string,
        sortOrder?: string
    ): Observable<ICertificateRecordsAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (sortBy) {
            params = params.set('sortBy', sortBy);
        }

        if (sortOrder) {
            params = params.set('sortOrder', sortOrder);
        }

        return this.http.get<ICertificateRecordsAndTotalCount>(`${this.environment.apiUrl}/users/${userId}/certificates`, { params });
    }

    notifySelected(payload: INotifySelectedParticipantsActionPayload): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/${this.entities}/notify-users`, payload);
    }

    teamsNotifySelected(teamId: string, payload: INotifySelectedParticipantsActionPayload): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/teams/${teamId}/${this.entities}/notify-users`, payload);
    }

    fetchNotificationKeywords(): Observable<string[]> {
        return this.http.get<string[]>(`${this.apiUrl}/${this.entities}/notification/keywords`);
    }
}
